/**
 * ChatMessageList Component
 * Displays chat messages with auto-scroll and conversation summaries
 */

import React, { useRef, useEffect } from 'react';
import { FiUser, FiCpu, FiLoader, FiBarChart2 } from 'react-icons/fi';
import type { ChatMessage, ConversationStats } from '../../types/chat.types';

interface ChatMessageListProps {
  messages: ChatMessage[];
  loading?: boolean;
  conversationStats?: ConversationStats | null;
  className?: string;
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  loading = false,
  conversationStats,
  className = ''
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (messages.length === 0 && !loading) {
    return (
      <div className={`h-full bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 flex items-center justify-center ${className}`}>
        <div className="text-center p-8">
          <FiCpu className="mx-auto text-gray-500 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-400 mb-2">No messages yet</h3>
          <p className="text-sm text-gray-500">
            Start a conversation to test your assistant
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 flex flex-col ${className}`}>
      {/* Conversation Stats Header */}
      {conversationStats && (
        <div className="p-3 border-b border-slate-700 bg-slate-900/50">
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <div className="flex items-center gap-1">
              <FiBarChart2 size={14} />
              <span>{conversationStats.messageCount} messages</span>
            </div>
            <div>•</div>
            <div>{conversationStats.avgResponseTime}ms avg response</div>
            <div>•</div>
            <div>{conversationStats.totalTokens} tokens</div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => {
          // Conversation summary message
          if (message.isConversationSummary && message.summary) {
            return (
              <div key={message.id} className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FiBarChart2 className="text-blue-400" size={16} />
                  <span className="text-sm font-medium text-blue-400">Conversation Summary</span>
                </div>
                <p className="text-sm text-gray-300 mb-3">{message.summary.summary}</p>

                {message.summary.keyTopics && message.summary.keyTopics.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {message.summary.keyTopics.map((topic, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          // Regular message
          const isUser = message.role === 'user';
          const isSimulated = message.isSimulated;

          return (
            <div
              key={message.id}
              className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}
            >
              {/* Avatar (for assistant) */}
              {!isUser && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <FiCpu className="text-blue-400" size={16} />
                </div>
              )}

              {/* Message Bubble */}
              <div
                className={`max-w-[70%] rounded-lg px-4 py-3 ${
                  isUser
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-700 text-gray-200'
                }`}
              >
                {/* Bot name for simulated messages */}
                {!isUser && isSimulated && message.botName && (
                  <div className="text-xs text-gray-400 mb-1">
                    {message.botName}
                  </div>
                )}

                {/* Message content */}
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>

                {/* Tool executions */}
                {message.toolExecutions && message.toolExecutions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-600 space-y-2">
                    {message.toolExecutions.map((tool, idx) => (
                      <div key={idx} className="text-xs">
                        <div className="font-medium text-blue-300">
                          🔧 {tool.toolName}
                        </div>
                        {tool.result && (
                          <div className="mt-1 text-gray-400 font-mono">
                            {JSON.stringify(tool.result, null, 2)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Timestamp */}
                <div className={`text-xs mt-2 ${isUser ? 'text-blue-100' : 'text-gray-500'}`}>
                  {new Date(message.timestamp).toLocaleTimeString()}
                </div>
              </div>

              {/* Avatar (for user) */}
              {isUser && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center">
                  <FiUser className="text-gray-300" size={16} />
                </div>
              )}
            </div>
          );
        })}

        {/* Loading indicator */}
        {loading && (
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
              <FiCpu className="text-blue-400" size={16} />
            </div>
            <div className="bg-slate-700 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2">
                <FiLoader className="animate-spin text-blue-400" size={16} />
                <span className="text-sm text-gray-300">Thinking...</span>
              </div>
            </div>
          </div>
        )}

        {/* Auto-scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatMessageList;
